<template>
	<UserPageWrapper :accountSettings="true">
		<Title>{{ $t("seo.pages.account.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.account.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="$t('seo.pages.account.title')" />
		<Meta property="og:description" :content="$t('seo.pages.account.description')" />
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<DjBox class="bg-gray-100 md:bg-white border-0 md:border pt-5 md:pt-8 px-5 md:px-10 pb-5 md:pb-10 md:mb-4">
			<BaseHeading type="h2" class="leading-3 text-center md:text-left mb-6">
				<span class="text-2xl">{{ $t("user.account.title") }}</span>
			</BaseHeading>

			<PartialProfileImageEditor
				v-if="isAuth"
				:avatar="avatar"
				:newImageName="`${useAuth.getUser().slug}`.toString() || null"
				@changedImage="changedImage"
			/>

			<BaseTextbox
				v-model="lastName"
				@keydown="clearErrors()"
				name="lastName"
				:label="$t('registration.field.lastName.label')"
				:placeholder="$t('registration.field.lastName.placeholder')"
				:error="
					lastNameError
						? $t(`errors.${lastNameError}`)
						: (serverFieldErrors['lastName'] && serverFieldErrors['lastName'][0]) || ''
				"
				width="100%"
				class="mt-4 mb-2"
				required
			/>

			<BaseTextbox
				v-model="firstName"
				@keydown="clearErrors()"
				name="firstName"
				:label="$t('registration.field.firstName.label')"
				:placeholder="$t('registration.field.firstName.placeholder')"
				:error="
					firstNameError
						? $t(`errors.${firstNameError}`)
						: (serverFieldErrors['firstName'] && serverFieldErrors['firstName'][0]) || ''
				"
				width="100%"
				class="mb-2"
				required
			/>

			<BaseTextbox
				v-model="email"
				@keydown="clearErrors()"
				type="email"
				name="email"
				:label="$t('user.field.email.label')"
				:placeholder="$t('user.field.email.placeholder')"
				:error="
					emailError
						? $t(`errors.${emailError}`)
						: (serverFieldErrors['email'] && serverFieldErrors['email'][0]) || ''
				"
				width="100%"
				class="mb-2"
				disabled
			/>

			<BasePhone
				id="notificationsModify"
				v-model="phone"
				@keydown="clearErrors()"
				:label="$t('user.field.phone.label')"
				width="100%"
				class="mb-2"
				:error="
					phoneError
						? $t(`errors.${phoneError}`)
						: (serverFieldErrors['phone_number'] && serverFieldErrors['phone_number'][0]) || ''
				"
			/>

			<!-- <BaseTextbox
                v-model="residence"
                @keydown="clearErrors()"
                name="residence"
                :label="$t('user.field.residence.label')"
                :placeholder="$t('user.field.residence.placeholder')"
                :error="residenceError ? $t(`errors.${residenceError}`) : (serverFieldErrors['residence'] && serverFieldErrors['residence'][0] || '')"
                width="100%"
                class="mb-2"
                required /> -->

			<div class="relative w-full" @click="clearErrors()">
				<PartialTextboxGooglePlaces
					v-if="isAuth && isMounted"
					v-model="residence"
					@place-changed="placeChanged"
					:label="$t('user.field.residence.label')"
					:placeholder="$t('user.field.residence.placeholder')"
				/>
				<div class="absolute bottom ml-dj-30 -mt-6 font-medium">
					<p v-if="residenceError.length > 0" class="text-dj-red text-xs">
						{{ $t(`errors.${residenceError}`) }}
					</p>
				</div>
			</div>

			<div class="relative w-full pb-6" @click="clearErrors()">
				<SearchbarJobsDetailedLevel
					v-if="loadedAccountData"
					class="border rounded-normal border-dj-gray-light bg-white px-dj-30 py-4"
					:class="{
						'border-dj-red hover:border-dj-red focus-within:border-dj-red focus-within:hover:border-dj-red':
							levelError.length > 0 || serverFieldErrors['job_levels'],
					}"
					v-model="level"
					:label="$t('registration.field.whichLevel')"
					:placeholder="$t('registration.field.whichLevelSelect')"
					:profileField="true"
				/>
				<div class="absolute bottom ml-dj-30 mt-1 font-medium">
					<p v-if="levelError.length > 0" class="text-dj-red text-xs">{{ $t(`errors.${levelError}`) }}</p>
					<p v-else-if="serverFieldErrors['job_levels']" class="text-dj-red text-xs">
						{{ serverFieldErrors["job_levels"][0] }}
					</p>
				</div>
			</div>

			<div class="relative w-full pb-6" @click="clearErrors()">
				<SearchbarJobsCategory
					v-if="loadedAccountData"
					class="border rounded-normal border-dj-gray-light bg-white px-dj-30 py-4"
					:class="{
						'border-dj-red hover:border-dj-red focus-within:border-dj-red focus-within:hover:border-dj-red':
							categoriesError.length > 0 || serverFieldErrors['job_categories'],
					}"
					v-model="categories"
					:label="$t('registration.field.whichJob')"
					:placeholder="$t('registration.field.whichJobSelect')"
					:profileField="true"
				/>
				<div class="absolute bottom ml-dj-30 mt-1 font-medium">
					<p v-if="categoriesError.length > 0" class="text-dj-red text-xs">
						{{ $t(`errors.${categoriesError}`) }}
					</p>
					<p v-else-if="serverFieldErrors['job_categories']" class="text-dj-red text-xs">
						{{ serverFieldErrors["job_categories"][0] }}
					</p>
				</div>
			</div>

			<div class="flex justify-center md:justify-end">
				<BaseButton @click="startSaveDataFlow" class="min-w-full sm:min-w-min px-12">
					{{ $t("user.save") }}
				</BaseButton>

				<p
					v-if="Object.keys(serverFieldErrors).length === 0 && serverErrorMsg && serverErrorMsg.length > 0"
					class="text-dj-red text-sm mt-2"
				>
					{{ serverErrorMsg }}
				</p>
			</div>
		</DjBox>

		<DjBox class="bg-gray-100 md:bg-white border-0 md:border pt-5 md:pt-8 px-5 md:px-10 pb-5">
			<span class="block text-lg font-medium mb-2">{{ $t("user.account.acceptedTerms") }}</span>

			<a class="leading-5" :href="getDocumentUrl(LegalDocType.ASZF, region, $i18n.locale)" target="_blank">
				{{ $t("user.account.downloadPdf") }}
			</a>

			<span class="block text-lg font-medium mt-6 mb-2">{{ $t("user.account.acceptedPrivacy") }}</span>
			<a class="leading-5" :href="getDocumentUrl(LegalDocType.PRIVACY, region, $i18n.locale)" target="_blank">
				{{ $t("user.account.downloadPdf") }}
			</a>
		</DjBox>

		<DjBox class="bg-gray-100 md:bg-white border-0 md:border pt-5 md:pt-8 px-5 md:px-10 pb-5">
			<span class="block text-lg font-medium mb-4">{{ $t("user.account.storedData") }}</span>
			<p class="mb-2">
				{{
					$t("user.account.storedDataInfo", {
						account: region.toLowerCase() == "ro" ? "business" : "info",
						domain: region.toLowerCase() == "ro" ? "dreamjobs.ro" : "dreamjobs.hu",
					})
				}}
			</p>
		</DjBox>

		<DjBox class="bg-gray-100 md:bg-white border-0 md:border pt-5 md:pt-8 px-5 md:px-10 pb-5">
			<span class="block text-lg font-medium mb-4">{{ $t("user.account.deleteProfile") }}</span>
			<p class="mb-4">{{ $t("user.account.deleteProfileInfo") }}</p>

			<button @click="showDeleteModal = true" class="mb-4 text-dj-red">
				{{ $t("user.account.deleteProfileButton") }}
			</button>
		</DjBox>

		<client-only>
			<BaseModal
				v-model="showDeleteModal"
				:title="$t('user.account.deleteProfile')"
				:acceptText="$t('user.delete')"
				:cancelText="$t('user.cancel')"
				@on-accept="onAcceptDelete"
			>
				<p class="mb-1">{{ $t("user.account.deleteProfileConfirmation") }}</p>
				<p class="mb-4">{{ $t("user.account.deleteProfileConfirmationInstr") }}</p>

				<BaseTextbox
					v-model="confirmationEmail"
					@keydown="clearErrors()"
					type="email"
					name="email"
					:label="$t('user.field.email.label')"
					:placeholder="$t('user.field.email.placeholder')"
					:error="
						confirmationEmailError
							? $t(`errors.${confirmationEmailError}`)
							: (serverFieldErrors['confirmationEmail'] && serverFieldErrors['confirmationEmail'][0]) ||
							  ''
					"
					width="100%"
					class="mb-2"
				/>

				<div class="flex justify-end">
					<BaseButton @click="onAcceptDelete" class="min-w-full sm:min-w-min px-12">
						{{ $t("user.delete") }}
					</BaseButton>
				</div>
			</BaseModal>
		</client-only>
	</UserPageWrapper>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { getDocumentUrl, LegalDocType } from "~/utils/legalDocs";

export default defineComponent({
	name: "UserAccountPage",
	setup() {
		definePageMeta({
			middleware: ["auth"],
		});
		const emailRegExp = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

		const { locale, t } = useI18n();
		const app = useNuxtApp();
		const region = ref("HU");

		const showDeleteModal = ref(false);
		const isAuth = useState("isAuth");
		const isMounted = ref(false);

		const loadedAccountData = ref(false);

		const preferredLang = ref("");
		const lastName = ref("");
		const firstName = ref("");
		const email = ref("");
		const phone = ref({ prefix: "36", number: "" });
		const residence = ref("");
		const country = ref("");
		const level = ref<number[]>([]);
		const categories = ref({ categories: [], subcategories: [] });

		const confirmationEmail = ref("");

		const avatar = ref(null);
		const lastNameError = ref("");
		const firstNameError = ref("");
		const emailError = ref("");
		const phoneError = ref("");
		const residenceError = ref("");
		const levelError = ref("");
		const categoriesError = ref("");

		const confirmationEmailError = ref("");

		const serverFieldErrors = ref({});
		const serverErrorMsg = ref("");

		function clearErrors() {
			lastNameError.value.length > 0 ? (lastNameError.value = "") : null;
			firstNameError.value.length > 0 ? (firstNameError.value = "") : null;
			emailError.value.length > 0 ? (emailError.value = "") : null;
			residenceError.value.length > 0 ? (residenceError.value = "") : null;
			levelError.value.length > 0 ? (levelError.value = "") : null;
			categoriesError.value.length > 0 ? (categoriesError.value = "") : null;

			confirmationEmailError.value.length > 0 ? (confirmationEmailError.value = "") : null;

			Object.keys(serverFieldErrors.value).length ? (serverFieldErrors.value = {}) : null;
			serverErrorMsg.value.length > 0 ? (serverErrorMsg.value = "") : null;
		}

		function startSaveDataFlow() {
			// Validations
			lastName.value.length === 0 ? (lastNameError.value = "requiredLastName") : null;
			firstName.value.length === 0 ? (firstNameError.value = "requiredFirstName") : null;
			!emailRegExp.test(email.value) ? (emailError.value = "invalidEmail") : null;
			email.value.length === 0 ? (emailError.value = "requiredEmail") : emailError.value;

			if (phone.value.prefix.length === 0 && phone.value.number.length === 0) {
				phoneError.value = "requiredPhone";
			}

			residence.value.length === 0 ? (residenceError.value = "requiredResidence") : null;

			level.value.length === 0 ? (levelError.value = "requiredLevel") : null;
			categories.value.categories.length === 0 ? (categoriesError.value = "requiredCategory") : null;

			if (
				(
					lastNameError.value +
					firstNameError.value +
					emailError.value +
					phoneError.value +
					residenceError.value +
					levelError.value +
					categoriesError.value
				).length < 1
			) {
				const userData = {
					email: email.value,
					last_name: lastName.value,
					first_name: firstName.value,
					phone: "+" + phone.value.prefix + phone.value.number,
					calling_code: phone.value.prefix,
					phone_number: phone.value.number,
					preferred_lang: preferredLang.value,
					job_categories: categories.value.categories,
					job_levels: level.value,
					city: residence.value,
					country: country.value,
				};
				saveData(userData);
			}
		}

		function saveData(userData) {
			$fetch(`/api/update-profile`, {
				// API for all profile update
				method: "PUT",
				body: {
					...userData,
					xsrf: useAuth.getXsrfToken(),
					token: useCookie("token").value,
					operation: "update-account-data",
					locale: locale.value,
					region: region.value,
				},
			})
				.then(() => {
					updateUser();
					try {
						app.$openToast({ message: t("user.successfulSave"), type: "success", description: "" });
					} catch (error) {
						console.error(error);
					}
				})
				.catch((error) => {
					if (error.response && error.response._data) {
						serverFieldErrors.value = error.response._data.data.errors || {};
					}
					if (error.response && error.response._data && error.response._data.message) {
						serverErrorMsg.value = error.statusText || error.response._data.message || "";
					}
				});
		}

		function onAcceptDelete() {
			if (confirmationEmail.value === email.value) {
				const userData = useAuth.getUser();
				$fetch(`/api/remove-user`, {
					method: "DELETE",
					body: {
						email: String(userData.email),
						xsrf: useAuth.getXsrfToken(),
						token: useCookie("token").value,
						locale: locale.value,
						region: region.value,
					},
				})
					.then(() => {
						try {
							app.$openToast({ message: t("user.successfulRemove"), type: "success", description: "" });
						} catch (error) {
							console.error(error);
						}
						useAuth.logout();
					})
					.catch((error) => {
						console.error("onAcceptDelete", error.message);

						try {
							app.$openToast({ message: error.message, type: "error", description: "" });
						} catch (error) {
							console.error(error);
						}
					});
			} else {
				confirmationEmailError.value = "invalidEmail";
			}
		}

		function placeChanged(place) {
			residence.value = place.city;
			country.value = place.country;
		}

		function getAccountData() {
			const userData = useAuth.getUser();

			avatar.value = userData.avatar;
			preferredLang.value = userData.preferred_lang;
			lastName.value = userData.vname;
			firstName.value = userData.kname;
			email.value = userData.email;
			level.value = userData.tags?.job_level?.data?.map((a) => a.id) ?? [];
			categories.value.categories = userData.tags?.job_category?.data?.map((a) => a.id) ?? [];
			phone.value = {
				prefix: userData.phone ? userData.phone.code || "36" : "36",
				number: userData.phone ? userData.phone.number || "" : "",
			};
			residence.value = userData.location ? userData.location.city || "" : "";
			country.value = userData.location ? userData.location.country || "" : "";

			loadedAccountData.value == false ? (loadedAccountData.value = true) : null;
		}

		function updateUser() {
			useAuth.updateUser().then(() => {
				getAccountData();
			});
		}

		function changedImage() {
			avatar.value = useAuth.getUser().avatar;
		}

		watch(isAuth, (val: boolean) => {
			val == true && getAccountData();
		});

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code;
			isAuth.value == true && getAccountData();
			isMounted.value = true;
		});

		return {
			showDeleteModal,
			region,
			isAuth,
			isMounted,
			loadedAccountData,
			avatar,
			lastName,
			firstName,
			email,
			phone,
			residence,
			placeChanged,
			level,
			categories,
			confirmationEmail,
			lastNameError,
			firstNameError,
			emailError,
			phoneError,
			residenceError,
			levelError,
			categoriesError,
			confirmationEmailError,
			serverErrorMsg,
			serverFieldErrors,
			clearErrors,
			startSaveDataFlow,
			onAcceptDelete,
			changedImage,
			getDocumentUrl,
			LegalDocType,
		};
	},
});
</script>
<style lang="scss" scoped></style>
